type FetchConfig = {
    method: string;
    credentials: RequestCredentials;
    signal?: AbortSignal | null | undefined
    headers?: {
        "Content-Type"?: string;
    };
    body?: FormData | string;
};

const setupErrorMessage = async (response: Response) => {
    // Essayer de récupérer le message d'erreur du corps de la réponse
    let errorMessage;

    try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error;
        } else {
            // Si c'est du texte brut (comme BadRequest retourne souvent)
            const textError = await response.text();
            if (textError) {
                errorMessage = textError;
            } else {
                errorMessage = `Erreur HTTP: ${response.status} - ${response.statusText}`;
            }
        }
    } catch (parseError) {
        errorMessage = `Erreur HTTP: ${response.status} - ${response.statusText}`;
    }

    return new Error(errorMessage);
}

const mutate =
    <T>(
        type: "PUT" | "POST" | "DELETE",
        endpoint: string,
        data: FormData | unknown,
    ): Promise<T | null> => new Promise<T | null>((resolvePromise, rejectPromise) => {

        const config: FetchConfig = {
            method: type,
            credentials: "include",
        };

        if (data instanceof FormData) {
            config.body = data;
        } else if (data) {
            config.headers = { "Content-Type": "application/json" }
            config.body = JSON.stringify(data);
        }

        fetch(endpoint, config).then(async (response) => {
            if (response.status === 401) {
                throw new Error("Non autorisé")
            }

            if (!response.ok) {
                throw await setupErrorMessage(response);
            }

            const text = await response.text();

            if (!text) {
                return resolvePromise(null);
            }

            const responseData: T = JSON.parse(text);
            return resolvePromise(responseData);
        }).catch((err) => {
            console.error(`Erreur lors de ${type} ${endpoint}:`, err);
            return rejectPromise(err);
        });
    })

export const fetchItem =
    <T>(
        endpoint: string,
    ): Promise<T | undefined> => new Promise<T | undefined>((resolvePromise, rejectPromise) => {
        const config: FetchConfig = {
            method: "GET",
            credentials: "include",
        };

        fetch(endpoint, config).then(async (response) => {
            if (response.status === 401) {
                throw new Error("Non autorisé")
            }

            if (!response.ok) {
                throw await setupErrorMessage(response);
            }

            if (response.status === 204) {
                return resolvePromise(undefined);
            }

            const text = await response.text();

            if (!text) {
                return resolvePromise(undefined);
            }

            const responseData: T = JSON.parse(text);
            return resolvePromise(responseData);
        }).catch((err) => {
            console.error(`Erreur lors du fetch ${endpoint}:`, err);
            return rejectPromise(err);
        });
    })

export const fetchBlob = (
    endpoint: string,
    data?: FormData | unknown,
    signal?: AbortSignal
): Promise<Blob | null> => new Promise<Blob | null>((resolvePromise) => {
    const config: FetchConfig = {
        method: data ? "POST" : "GET",
        credentials: "include",
        signal
    };

    if (data instanceof FormData) {
        config.body = data;
    } else if (data) {
        config.headers = { "Content-Type": "application/json" }
        config.body = JSON.stringify(data);
    }

    fetch(endpoint, config).then(async (response) => {
        if (!response.ok) {
            throw new Error(
                `Erreur HTTP: ${response.status} - ${response.statusText}`,
            );
        }

        if (response.status === 204) {
            return resolvePromise(null);
        }

        const blob = await response.blob();
        return resolvePromise(blob);
    });
});

export const postItem = <T>(
    endpoint: string,
    data?: FormData | unknown,
) => mutate<T>("POST", endpoint, data);

export const putItem = <T>(
    endpoint: string,
    data: FormData | unknown,
) => mutate<T>("PUT", endpoint, data);

export const deleteItem = <T>(
    endpoint: string,
    data?: FormData | unknown,
) => mutate<T>("DELETE", endpoint, data);